import {catalog, partnerIntegrations} from '../constants';
import {getValueIn} from './ValueUtils';
import {
    CatalogItem as GraphQlCatalogItem,
    CustomAttribute,
    Category as PathCategory
} from 'epic-graphql-types';
import {CustomProp, KeyImageType, Category} from '../constants/Catalog';
import {CatalogItemsMap} from '../redux/reducers/catalog.reducer';
import {CatalogItem, CustomAttributes, KeyImages} from '../models/CatalogItem';
import {createClassLogger, Logger} from './Logging';
import {pathOr, pick} from 'ramda';

const logger: Logger = createClassLogger('CatalogUtils');

export interface LinkedOfferCatalogItem extends GraphQlCatalogItem {
    linkedOffer?: {
        customAttributes?: CustomAttribute[];
    };
}

export const getGameBoxKeyImage = (catalogItem: CatalogItem): string => {
    const urls: string[] | undefined =
        catalogItem &&
        catalogItem.keyImages &&
        catalogItem.keyImages[catalog.keyImageTypes.GAMEBOX];
    return urls && urls.length ? urls[0] : '';
};

export const getGameBoxLogoKeyImage = (catalogItem: CatalogItem): string => {
    const urls: string[] | undefined =
        catalogItem &&
        catalogItem.keyImages &&
        catalogItem.keyImages[catalog.keyImageTypes.GAMEBOX_LOGO];
    return urls && urls.length ? urls[0] : '';
};

export const getAvailableDate = (catalogItem: CatalogItem): Date | undefined => {
    const availableDate: string | undefined =
        catalogItem &&
        catalogItem.customAttributes &&
        catalogItem.customAttributes[catalog.customProps.AVAILABLE_DATE];

    if (!availableDate) {
        return undefined;
    }

    try {
        const availableDateSeconds: number = parseInt(availableDate);
        const date: Date = new Date(0);
        date.setUTCSeconds(availableDateSeconds);
        return date;
    } catch (ex) {
        logger.warn('getAvailableDate', 'Error converting availableDate to a Date', {
            availableDate
        });
        return undefined;
    }
};

export const isHiddenCatalogItem = (catalogItem: CatalogItem): boolean | undefined =>
    catalogItem &&
    catalogItem.categories &&
    catalogItem.categories.includes(catalog.categories.HIDDEN);

export const isGameCatalogItem = (catalogItem: CatalogItem): boolean | undefined =>
    catalogItem &&
    catalogItem.categories &&
    !catalogItem.categories.includes(catalog.categories.DEPRECATED) &&
    !catalogItem.categories.includes(catalog.categories.HIDDEN) &&
    catalogItem.categories.includes(catalog.categories.GAMES) &&
    catalogItem.categories.includes(catalog.categories.APPLICATIONS);

export const isModCatalogItem = (catalogItem: CatalogItem): boolean | undefined =>
    catalogItem &&
    catalogItem.categories &&
    !catalogItem.categories.includes(catalog.categories.DEPRECATED) &&
    !catalogItem.categories.includes(catalog.categories.HIDDEN) &&
    catalogItem.categories.includes(catalog.categories.ENGINES) &&
    catalogItem.categories.includes(catalog.categories.APPLICATIONS);

export const isDLCCatalogItem = (catalogItem: CatalogItem): boolean => {
    return !!(catalogItem && catalogItem.mainGameItem && catalogItem.mainGameItem.id);
};

export const isValidCatalogItem = (catalogItem: CatalogItem): boolean | undefined =>
    (isGameCatalogItem(catalogItem) || isModCatalogItem(catalogItem)) &&
    !isDLCCatalogItem(catalogItem);

export const isCatalogItemUnsupported = (catalogItem: CatalogItem, platform: string): boolean => {
    if (!catalogItem || !platform) return false; // cannot tell if it's unsupported, so default to supported
    const releaseInfoPlatforms = getValueIn(catalogItem, 'releaseInfo', 0, 'platform');
    return !releaseInfoPlatforms || !releaseInfoPlatforms.includes(platform);
};

export const getCustomProp = (catalogItem: CatalogItem, customProp: string): string | undefined => {
    return catalogItem && catalogItem.customAttributes && catalogItem.customAttributes[customProp];
};

export const isCatalogItemUplay = (catalogItem: CatalogItem): boolean => {
    if (catalogItem && catalogItem.customAttributes) {
        const partnerIdType: string | undefined = getCustomProp(
            catalogItem,
            catalog.customProps.PARTNER_LINK_TYPE
        );
        return partnerIdType === partnerIntegrations.partnerTypes.UBISOFT;
    }
    return false;
};

export const getPartnerLinkType = (catalogItem: CatalogItem): string | undefined => {
    return getCustomProp(catalogItem, catalog.customProps.PARTNER_LINK_TYPE);
};

export const getPartnerLinkId = (catalogItem: CatalogItem): string | undefined => {
    return getCustomProp(catalogItem, catalog.customProps.PARTNER_LINK_ID);
};

export const getParentPartnerLinkId = (catalogItem: CatalogItem): string | undefined => {
    return getCustomProp(catalogItem, catalog.customProps.PARENT_PARTNER_LINK_ID);
};

const reduceCustomAttributes = (
    acc: CustomAttributes,
    props: {key: CustomProp; value: string}
): CustomAttributes => {
    acc[props.key] = props.value;
    return acc;
};

/**
 * Converts a graphQl catalog item to a CatalogItem that the web client is expecting
 */
export const adaptCatalogItem = (graphQlCatalogItem: GraphQlCatalogItem): CatalogItem => {
    if (!graphQlCatalogItem) {
        return {};
    }

    /*    const {
        categories,
        customAttributes,
        keyImages,
        dlcItemList,
        mainGameItem,
        ...otherProps
    } = graphQlCatalogItem;

    const out: CatalogItem = {...otherProps};*/
    const customAttributes = pathOr([], ['customAttributes'], graphQlCatalogItem).reduce(
        reduceCustomAttributes,
        {}
    );

    const keyImages = pathOr([], ['keyImages'], graphQlCatalogItem).reduce(
        (acc: KeyImages, props: {type: KeyImageType; url: string}) => {
            const images = acc[props.type] || [];
            images.push(props.url);
            return {
                ...acc,
                [props.type]: images
            };
        },
        {}
    );

    const categories = pathOr([], ['categories'], graphQlCatalogItem).map(
        (x: PathCategory): Category => x.path as Category
    );

    const dlcItemList = pathOr([], ['dlcItemList'], graphQlCatalogItem).map(x => ({id: x.id}));

    const mainGameItem = pick(['id'], pathOr([], ['mainGameItem'], graphQlCatalogItem));

    const linkedOffer = {
        customAttributes: pathOr(
            [],
            ['linkedOffer', 'customAttributes'],
            graphQlCatalogItem
        ).reduce(reduceCustomAttributes, {})
    };

    return {
        ...(graphQlCatalogItem as CatalogItem),
        categories,
        customAttributes,
        dlcItemList,
        keyImages,
        linkedOffer,
        mainGameItem
    };
};

export const getOwnedCatalogItemDLCItems = (
    catalogItem: CatalogItem,
    catalogItems: CatalogItemsMap
): CatalogItem[] => {
    if (!catalogItem || !catalogItem.dlcItemList || !catalogItems || !catalogItems.size) return [];
    return catalogItem.dlcItemList.reduce((acc: CatalogItem[], item: {id: string}) => {
        if (catalogItems.has(item.id) && !isHiddenCatalogItem(catalogItems.get(item.id))) {
            acc.push(catalogItems.get(item.id));
        }
        return acc;
    }, []);
};
